import { getWithAuth } from '@/lib/httpAdapter';
import type User from '@/types/userType';
import apiUrl from './api';

/** team_uuid value selecting members who belong to no team. */
export const NO_TEAM = '__none__';

/**
 * A deep first window so the table fills in one request, then shallower ones as
 * the reader scrolls. The server caps `limit` at 200.
 */
export const ORG_MEMBERS_FIRST_LIMIT = 200;
export const ORG_MEMBERS_NEXT_LIMIT = 100;

/**
 * A member row. Extends `User` so every existing consumer (team selector, edit
 * and delete flows, role dropdown) takes it unchanged; `TeamName` is the join
 * the endpoint resolves server-side to kill the per-team request fan-out.
 */
export interface OrgMemberRow extends User {
  TeamName: string;
}

export interface OrgTeamFacet {
  TeamUUID: string;
  TeamName: string;
  MemberCount: number;
}

export interface OrgMembersPage {
  success: boolean;
  users: OrgMemberRow[];
  total: number;
  offset: number;
  limit: number;
  has_more: boolean;
  /** Present at offset 0 only. Identical for every window of a given filter. */
  teams?: OrgTeamFacet[];
  /** Present at offset 0 only. */
  provider?: string;
}

/**
 * One window of the current org's members, each row carrying its team name.
 *
 * The org comes from the auth token, so there is no org parameter to pass or to
 * get wrong. Replaces the Team tab's fetch-all-users plus one-request-per-team
 * fan-out.
 */
export const fetchOrgMembers = async ({
  offset,
  limit = ORG_MEMBERS_FIRST_LIMIT,
  search,
  teamUUID,
  role,
}: {
  offset: number;
  limit?: number;
  search?: string;
  teamUUID?: string;
  role?: string;
}): Promise<OrgMembersPage> => {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  });
  // Empty filters are omitted rather than sent blank: it keeps the URL, and so
  // the react-query cache entry, identical to the unfiltered request.
  if (search) params.set('search', search);
  if (teamUUID) params.set('team_uuid', teamUUID);
  if (role) params.set('role', role);

  const response = await getWithAuth(
    `${apiUrl}organization/members/?${params.toString()}`
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return response.json();
};
